import React from 'react';
import { Platform } from 'react-native';
import globalEventBus from './EventBus';

/**
 * Web 三栏布局导航助手
 * 
 * 使用方法：
 * 1. 在 src/config/webNavigableScreens.ts 注册页面
 * 2. 在需要导航的地方调用 WebNavigationHelper.navigate()
 * 3. 或者直接发送 ShowWebDetail 事件
 * 
 * 优势：
 * - 配置化：新增页面只需添加配置
 * - 类型安全：TypeScript 全程支持
 * - 自动处理：平台差异自动处理
 */

// 定义可在第三栏显示的页面配置
export type WebNavigableScreen = {
    name: string;
    component: React.ComponentType<any>;
    hideBackButton?: boolean; // Web 环境下是否隐藏返回按钮
    tabType?: 'Contact' | 'Chat' | 'Mine'; // 属于哪个 tab
};

// 页面注册表
const screenRegistry = new Map<string, WebNavigableScreen>();

/**
 * 注册页面到 Web 导航系统
 */
export function registerScreen(config: WebNavigableScreen) {
    screenRegistry.set(config.name, config);
}

/**
 * 批量注册页面
 */
export function registerScreens(configs: WebNavigableScreen[]) {
    configs.forEach(config => registerScreen(config));
}

/**
 * 获取已注册的页面配置
 */
export function getScreenConfig(name: string): WebNavigableScreen | undefined {
    return screenRegistry.get(name);
}

/**
 * 获取所有已注册的页面
 */
export function getAllScreens(): WebNavigableScreen[] {
    return Array.from(screenRegistry.values());
}

/**
 * Web 导航助手
 */
export const WebNavigationHelper = {
    /**
     * 智能导航：Web 环境下在第三栏显示，移动端正常跳转
     * @param navigation React Navigation 的 navigation 对象
     * @param screenName 要导航到的页面名称
     * @param params 导航参数
     */
    navigate(navigation: any, screenName: string, params?: any) {
        if (Platform.OS === 'web') {
            const config = getScreenConfig(screenName);
            if (config) {
                // 在第三栏显示
                // 确保 params 是一个对象，避免传递 undefined 或 null
                const safeParams = params && typeof params === 'object' ? params : {};
                globalEventBus.emit('ShowWebDetail', {
                    screenName,
                    params: safeParams,
                    tabType: config.tabType
                });
                return;
            }
        }
        // 移动端或未注册的页面，使用正常导航
        navigation.navigate(screenName, params);
    },

    /**
     * 返回上一页（Web 环境下清空第三栏）
     */
    goBack(navigation: any) {
        if (Platform.OS === 'web') {
            globalEventBus.emit('ClearWebDetail', {});
        } else {
            navigation.goBack();
        }
    },

    /**
     * 判断页面是否应该隐藏返回按钮
     */
    shouldHideBackButton(screenName: string): boolean {
        if (Platform.OS !== 'web') {
            return false;
        }
        const config = getScreenConfig(screenName);
        return config?.hideBackButton ?? true; // 默认在 Web 上隐藏
    }
};

/**
 * HOC: 为页面添加 Web 导航支持
 * 自动处理返回按钮的显示/隐藏
 */
export function withWebNavBar<P extends object>(
    WrappedComponent: React.ComponentType<P>,
    screenName: string
) {
    return (props: P) => {
        const shouldHideBack = WebNavigationHelper.shouldHideBackButton(screenName);
        
        return (
            <WrappedComponent
                {...props}
                webNavigable={true}
                hideBackButton={shouldHideBack}
            />
        );
    };
}

// 导出工具类型
export type WebDetailPayload = {
    screenName: string;
    params?: any;
    tabType?: 'Contact' | 'Chat' | 'Mine';
};
